import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const emptyScent = {
  symbol: "",
  name: "",
  category: "Floral",
  description: "",
  color: "#FF8FAB",
  image_url: "",
  notes: [],
  bestseller: false,
};

const emptyEvent = { tag: "Pop-up", title: "", date_text: "", description: "", order: 99 };
const emptyFB = { name: "", category: "Sweet", tagline: "", scent_symbols: [], order: 99 };

const STATUSES = ["new", "contacted", "paid", "shipped", "fulfilled", "cancelled"];
const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

const TrackingInputRow = ({ order, onSave }) => {
  const [tn, setTn] = React.useState(order.tracking_number || "");
  const [car, setCar] = React.useState(order.carrier || "USPS");
  return (
    <div className="flex items-center gap-2">
      <select
        data-testid={`carrier-${order.id}`}
        value={car}
        onChange={(e) => setCar(e.target.value)}
        className="border border-black bg-white text-xs px-2 py-1"
      >
        {CARRIERS.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <input
        data-testid={`tracking-${order.id}`}
        value={tn}
        onChange={(e) => setTn(e.target.value)}
        placeholder="Tracking number"
        className="flex-1 border-b border-black bg-transparent py-1 text-xs outline-none focus:border-[#FF1493]"
      />
      <button
        data-testid={`save-tracking-${order.id}`}
        onClick={() => onSave(order.id, tn, car)}
        className="overline text-[10px] bg-[#FF1493] text-white px-3 py-1 border border-black hover:bg-black"
      >
        Save & email
      </button>
    </div>
  );
};

const AdminDashboard = () => {
  const token = localStorage.getItem("briology_token");
  const nav = useNavigate();
  const [scents, setScents] = useState([]);
  const [blends, setBlends] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingDraft, setPricingDraft] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [capDraft, setCapDraft] = useState("");

  const [editingScent, setEditingScent] = useState(null);
  const [scentForm, setScentForm] = useState(emptyScent);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [editingFB, setEditingFB] = useState(null);
  const [fbForm, setFbForm] = useState(emptyFB);

  const refresh = async () => {
    try {
      const [s, b, o, r, e, f, a, p, w, inv] = await Promise.all([
        api.get("/scents"),
        api.get("/blends"),
        api.get("/orders"),
        api.get("/event-rsvps"),
        api.get("/events-list"),
        api.get("/featured-blends"),
        api.get("/analytics/summary"),
        api.get("/pricing"),
        api.get("/waitlist"),
        api.get("/inventory"),
      ]);
      setScents(s.data);
      setBlends(b.data);
      // defensive dedupe by id (handles any historic test-data dupes)
      const seen = new Set();
      setOrders(o.data.filter((x) => (seen.has(x.id) ? false : seen.add(x.id))));
      setRsvps(r.data);
      setEvents(e.data);
      setFeatured(f.data);
      setAnalytics(a.data);
      setPricing(p.data);
      // Attach a stable client-side _key to each editable pricing row so React
      // preserves input focus while the admin edits labels (using array index
      // as key would drop focus every keystroke).
      const withKeys = (obj) => {
        const clone = JSON.parse(JSON.stringify(obj));
        (clone.sizes || []).forEach((s) => (s._key = s._key || crypto.randomUUID()));
        (clone.event_packages || []).forEach((p) => (p._key = p._key || crypto.randomUUID()));
        return clone;
      };
      setPricingDraft(withKeys(p.data));
      setWaitlist(w.data);
      setInventory(inv.data);
      setCapDraft(String(inv.data.cap));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("briology_token");
        nav("/admin");
      }
    }
  };

  useEffect(() => {
    if (token) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) return <Navigate to="/admin" replace />;

  const logout = () => {
    localStorage.removeItem("briology_token");
    nav("/admin");
  };

  // ----- Scent handlers -----
  const openNewScent = () => {
    setEditingScent({ new: true });
    setScentForm(emptyScent);
  };
  const openEditScent = (s) => {
    setEditingScent(s);
    setScentForm({ ...s, notes: s.notes || [] });
  };
  const saveScent = async () => {
    try {
      const payload = { ...scentForm };
      if (typeof payload.notes === "string") {
        payload.notes = payload.notes
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }
      if (editingScent.new) {
        await api.post("/scents", payload);
        toast.success("Scent added");
      } else {
        await api.put(`/scents/${editingScent.id}`, payload);
        toast.success("Scent updated");
      }
      setEditingScent(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteScent = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try {
      await api.delete(`/scents/${s.id}`);
      toast.success("Deleted");
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };
  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) return toast.error("Keep image under 1MB");
    const dataUrl = await fileToDataUrl(file);
    setScentForm((f) => ({ ...f, image_url: dataUrl }));
  };

  // ----- Order handlers -----
  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const saveTracking = async (orderId, tracking_number, carrier) => {
    if (!tracking_number?.trim()) {
      toast.error("Enter a tracking number");
      return;
    }
    try {
      await api.patch(`/orders/${orderId}`, { tracking_number, carrier });
      toast.success("Tracking saved · customer notified");
      refresh();
    } catch {
      toast.error("Tracking save failed");
    }
  };

  // Stripe payment status is handled by the Stripe checkout session; nothing to regenerate here.

  // ----- Event handlers -----
  const openNewEvent = () => {
    setEditingEvent({ new: true });
    setEventForm(emptyEvent);
  };
  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventForm({ ...ev });
  };
  const saveEvent = async () => {
    try {
      if (editingEvent.new) {
        await api.post("/events-list", eventForm);
      } else {
        await api.put(`/events-list/${editingEvent.id}`, eventForm);
      }
      toast.success("Saved");
      setEditingEvent(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete event "${ev.title}"?`)) return;
    try {
      await api.delete(`/events-list/${ev.id}`);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  // ----- Featured blend handlers -----
  const openNewFB = () => {
    setEditingFB({ new: true });
    setFbForm(emptyFB);
  };
  const openEditFB = (fb) => {
    setEditingFB(fb);
    setFbForm({ ...fb });
  };
  const saveFB = async () => {
    try {
      const payload = { ...fbForm };
      if (typeof payload.scent_symbols === "string") {
        payload.scent_symbols = payload.scent_symbols
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }
      if (editingFB.new) {
        await api.post("/featured-blends", payload);
      } else {
        await api.put(`/featured-blends/${editingFB.id}`, payload);
      }
      toast.success("Saved");
      setEditingFB(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteFB = async (fb) => {
    if (!window.confirm(`Delete "${fb.name}"?`)) return;
    try {
      await api.delete(`/featured-blends/${fb.id}`);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveCap = async () => {
    const n = parseInt(capDraft, 10);
    if (!Number.isFinite(n) || n < 0 || String(n) !== capDraft.trim()) {
      toast.error("Cap must be a non-negative integer");
      return;
    }
    if (n > 10000) {
      toast.error("Cap cannot exceed 10,000");
      return;
    }
    try {
      const { data } = await api.put("/inventory/cap", { cap: n });
      setInventory(data);
      setCapDraft(String(data.cap));
      toast.success(`Cap updated · ${data.sold} / ${data.cap}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const resetInventory = async () => {
    if (
      !window.confirm(
        "Start a new drop? The sold counter will reset to 0. Existing order history stays intact."
      )
    ) {
      return;
    }
    try {
      const { data } = await api.post("/inventory/reset");
      setInventory(data);
      setCapDraft(String(data.cap));
      toast.success("New drop started — counter reset to 0");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const newOrdersCount = orders.filter((o) => o.status === "new").length;

  const savePricing = async () => {
    try {
      // Strip client-side _key markers before PUT — backend Pydantic ignores
      // unknown fields but we send a clean payload for hygiene.
      const clean = JSON.parse(JSON.stringify(pricingDraft));
      (clean.sizes || []).forEach((s) => delete s._key);
      (clean.event_packages || []).forEach((p) => delete p._key);
      await api.put("/pricing", clean);
      toast.success("Pricing saved");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-[10px] text-[#FF1493]">Briology Admin</div>
          <h1 className="font-serif-display text-5xl font-black">Control room</h1>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="overline text-[10px] border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors"
        >
          Sign out
        </button>
      </div>

      {/* Inventory tracker */}
      {inventory && (
        <div
          data-testid="admin-inventory-card"
          className="mb-8 border border-black p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ backgroundColor: inventory.sold_out ? "#FFE4E4" : "#FFF1F5" }}
        >
          <div>
            <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-1">
              Batch inventory
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div
                data-testid="admin-inventory-sold"
                className="font-serif-display text-4xl font-black leading-none"
              >
                {inventory.sold}
                <span className="text-black/40"> / {inventory.cap}</span>
              </div>
              <div className="text-sm text-black/70">
                bottles sold
                {inventory.sold_out ? (
                  <span
                    data-testid="admin-inventory-status"
                    className="ml-2 overline text-[10px] tracking-[0.22em] bg-black text-white px-2 py-0.5"
                  >
                    SOLD OUT · checkout disabled
                  </span>
                ) : (
                  <span
                    data-testid="admin-inventory-status"
                    className="ml-2 overline text-[10px] tracking-[0.22em] text-black/60"
                  >
                    {inventory.remaining} left
                  </span>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 w-full md:w-96 h-2 bg-black/10 border border-black/30">
              <div
                data-testid="admin-inventory-bar"
                className="h-full bg-[#FF1493]"
                style={{
                  width: `${Math.min(100, Math.round((inventory.sold / Math.max(1, inventory.cap)) * 100))}%`,
                }}
              />
            </div>
            {inventory.batch_started_at && (
              <div
                data-testid="admin-inventory-batch-started"
                className="mt-2 text-[10.5px] text-black/50 italic"
              >
                Drop started · {new Date(inventory.batch_started_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="overline text-[10px] block mb-1">
                Cap
              </label>
              <input
                data-testid="admin-inventory-cap-input"
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                className="border border-black bg-white px-3 py-2 w-24 font-serif-display text-lg text-center outline-none focus:bg-[#FFF1F5]"
              />
            </div>
            <button
              data-testid="admin-inventory-cap-save"
              onClick={saveCap}
              className="bg-black text-white overline text-[10px] tracking-[0.22em] px-4 py-2 hover:bg-[#FF1493] transition-colors"
            >
              Update cap
            </button>
            <button
              data-testid="admin-inventory-reset-btn"
              onClick={resetInventory}
              className="bg-[#FF1493] text-white overline text-[10px] tracking-[0.22em] px-4 py-2 border border-black hover:bg-black transition-colors"
            >
              Start new drop
            </button>
          </div>
        </div>
      )}

      <Tabs defaultValue={newOrdersCount > 0 ? "orders" : "scents"} className="w-full">
        <TabsList className="bg-transparent border border-black rounded-none p-0 h-auto flex-wrap">
          {[
            ["orders", `Orders${newOrdersCount ? ` (${newOrdersCount})` : ""}`],
            ["waitlist", `Waitlist (${waitlist.length})`],
            ["scents", "Scents"],
            ["featured", "Popular Blends"],
            ["events", "Events"],
            ["pricing", "Pricing"],
            ["rsvps", "RSVPs"],
            ["blends", "Saved blends"],
            ["analytics", "Analytics"],
          ].map(([v, label]) => (
            <TabsTrigger
              key={v}
              data-testid={`tab-${v}`}
              value={v}
              className="rounded-none border-r border-black last:border-r-0 data-[state=active]:bg-black data-[state=active]:text-white overline text-[11px] px-5 py-2"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6">
          <h2 className="font-serif-display text-2xl font-bold mb-4">
            Customer orders ({orders.length})
          </h2>
          {orders.length === 0 ? (
            <div className="text-black/50 border border-dashed border-black/40 p-8 text-center">
              No orders yet.
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div
                  key={o.id}
                  data-testid={`order-${o.id}`}
                  className="border border-black bg-white p-5"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="overline text-[10px] text-[#FF1493]">
                        {o.status.toUpperCase()} ·{" "}
                        {new Date(o.created_at).toLocaleString()}
                      </div>
                      <div className="font-serif-display text-2xl font-black">
                        {o.blend_name}
                      </div>
                      <div className="text-sm font-semibold mt-0.5">
                        {o.bottle_size || "20ml"} · ${(o.price_total ?? 0).toFixed(2)}
                        {o.shipping_method && (
                          <span
                            data-testid={`order-shipping-method-${o.id}`}
                            className={[
                              "ml-2 overline text-[9.5px] tracking-[0.22em] px-2 py-0.5 border border-black align-middle",
                              o.shipping_method === "expedited"
                                ? "bg-[#FF1493] text-white border-[#FF1493]"
                                : "bg-white text-black",
                            ].join(" ")}
                          >
                            {o.shipping_method === "expedited"
                              ? "Expedited · 1-4d"
                              : "Standard · 3-7d"}
                          </span>
                        )}
                        {o.promo_code && (
                          <span className="ml-2 overline text-[9.5px] tracking-[0.22em] px-2 py-0.5 bg-black text-white align-middle">
                            {o.promo_code} · −${(o.discount ?? 0).toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-black/70 mt-1">
                        for {o.customer_name} · {o.customer_email}
                        {o.customer_phone ? ` · ${o.customer_phone}` : ""}
                      </div>
                    </div>
                    <select
                      data-testid={`order-status-${o.id}`}
                      value={o.status}
                      onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                      className="border border-black bg-white text-xs px-2 py-1"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
                    <div>
                      <div className="overline text-[9px] text-black/60 mb-1">
                        Notes
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {o.scent_ids.map((id) => {
                          const sc = scents.find((s) => s.id === id);
                          return (
                            <span
                              key={id}
                              className="text-[10px] overline border border-black/40 px-1.5 py-0.5"
                            >
                              {sc ? `${sc.symbol} ${sc.name}` : "?"}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="overline text-[9px] text-black/60 mb-1">
                        Shipping
                      </div>
                      <div className="text-xs text-black/80 whitespace-pre-line">
                        {[
                          o.shipping_street,
                          [o.shipping_city, o.shipping_state, o.shipping_zip]
                            .filter(Boolean)
                            .join(", "),
                          o.shipping_country,
                        ]
                          .filter(Boolean)
                          .join("\n") || "(none — local pickup?)"}
                      </div>
                    </div>
                    <div>
                      <div className="overline text-[9px] text-black/60 mb-1">
                        Payment
                      </div>
                      <div className="text-xs">
                        <span className="font-semibold">Stripe</span>
                        {o.stripe_payment_status ? ` · ${o.stripe_payment_status}` : ""}
                      </div>
                      {o.notes && (
                        <div className="text-xs text-black/60 mt-2 italic">
                          “{o.notes}”
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stripe session + tracking row */}
                  <div className="mt-4 pt-4 border-t border-black/15 grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="overline text-[9px] text-black/60 mb-1">
                        Stripe session
                      </div>
                      {o.stripe_session_id ? (
                        <div className="flex items-center gap-2">
                          <span
                            data-testid={`stripe-session-${o.id}`}
                            className="text-xs text-black/70 break-all flex-1 font-mono"
                          >
                            {o.stripe_session_id}
                          </span>
                          <button
                            onClick={() =>
                              navigator.clipboard
                                ?.writeText(o.stripe_session_id)
                                .then(() => toast.success("Copied"))
                            }
                            className="overline text-[10px] underline whitespace-nowrap"
                          >
                            copy
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-black/50 italic">
                          No Stripe session on record
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="overline text-[9px] text-black/60 mb-1">
                        Shipping tracking
                      </div>
                      <TrackingInputRow
                        order={o}
                        onSave={saveTracking}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SCENTS */}
        <TabsContent value="scents" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl font-bold">
              Periodic table ({scents.length})
            </h2>
            <button
              data-testid="new-scent-btn"
              onClick={openNewScent}
              className="bg-[#FF1493] text-white overline text-[11px] px-5 py-2 border border-black hover:bg-black"
            >
              + Add scent
            </button>
          </div>
          <div className="border border-black overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  {["#", "Symbol", "Name", "Category", "★", "Color", "Image", ""].map((h) => (
                    <th key={h} className="text-left overline text-[10px] px-3 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scents.map((s) => (
                  <tr
                    key={s.id}
                    data-testid={`row-${s.symbol}`}
                    className="border-t border-black/15"
                  >
                    <td className="px-3 py-2">{s.atomic_number}</td>
                    <td className="px-3 py-2 font-serif-display font-bold">
                      {s.symbol}
                    </td>
                    <td className="px-3 py-2">{s.name}</td>
                    <td className="px-3 py-2">{s.category}</td>
                    <td className="px-3 py-2">{s.bestseller ? "★" : ""}</td>
                    <td className="px-3 py-2">
                      <span
                        className="inline-block w-5 h-5 border border-black align-middle mr-2"
                        style={{ background: s.color }}
                      />
                      <span className="text-xs">{s.color}</span>
                    </td>
                    <td className="px-3 py-2">
                      {s.image_url ? (
                        <img
                          src={s.image_url}
                          alt={s.name}
                          className="w-8 h-8 object-cover border border-black"
                        />
                      ) : (
                        <span className="text-black/40 text-xs">none</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        data-testid={`edit-${s.symbol}`}
                        onClick={() => openEditScent(s)}
                        className="overline text-[10px] underline mr-3"
                      >
                        edit
                      </button>
                      <button
                        data-testid={`delete-${s.symbol}`}
                        onClick={() => deleteScent(s)}
                        className="overline text-[10px] text-[#FF1493] underline"
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* FEATURED BLENDS */}
        <TabsContent value="featured" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl font-bold">
              Popular blends ({featured.length})
            </h2>
            <button
              data-testid="new-fb-btn"
              onClick={openNewFB}
              className="bg-[#FF1493] text-white overline text-[11px] px-5 py-2 border border-black hover:bg-black"
            >
              + Add blend
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {featured.map((fb) => (
              <div
                key={fb.id}
                className="border border-black bg-white p-4 flex items-start justify-between gap-3"
                data-testid={`fb-row-${fb.id}`}
              >
                <div>
                  <div className="overline text-[10px] text-[#FF1493]">
                    {fb.category} · order {fb.order}
                  </div>
                  <div className="font-serif-display text-xl font-black">
                    {fb.name}
                  </div>
                  {fb.tagline && (
                    <div className="text-xs italic text-black/60 mt-0.5">
                      {fb.tagline}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {fb.scent_symbols.map((sym) => (
                      <span
                        key={sym}
                        className="text-[10px] overline border border-black/40 px-1.5 py-0.5"
                      >
                        {sym}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEditFB(fb)}
                    className="overline text-[10px] underline"
                  >
                    edit
                  </button>
                  <button
                    onClick={() => deleteFB(fb)}
                    className="overline text-[10px] text-[#FF1493] underline"
                  >
                    delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl font-bold">
              Events ({events.length})
            </h2>
            <button
              data-testid="new-event-btn"
              onClick={openNewEvent}
              className="bg-[#FF1493] text-white overline text-[11px] px-5 py-2 border border-black hover:bg-black"
            >
              + Add event
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="border border-black bg-white p-4 flex items-start justify-between gap-3"
                data-testid={`event-row-${ev.id}`}
              >
                <div>
                  <div className="overline text-[10px] text-[#FF1493]">
                    {ev.tag} · {ev.date_text}
                  </div>
                  <div className="font-serif-display text-xl font-black">
                    {ev.title}
                  </div>
                  <div className="text-xs text-black/70 mt-1 line-clamp-2">
                    {ev.description}
                  </div>
                </div>
                <div className="flex flex-col gap-1 text-right whitespace-nowrap">
                  <button
                    onClick={() => openEditEvent(ev)}
                    className="overline text-[10px] underline"
                  >
                    edit
                  </button>
                  <button
                    onClick={() => deleteEvent(ev)}
                    className="overline text-[10px] text-[#FF1493] underline"
                  >
                    delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* PRICING */}
        <TabsContent value="pricing" className="mt-6">
          <h2 className="font-serif-display text-2xl font-bold mb-4">
            Pricing & packages
          </h2>
          {!pricingDraft ? (
            <div className="text-black/50">Loading…</div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Bottle sizes */}
              <div className="border border-black bg-white p-5">
                <h3 className="overline text-[10px] mb-3">Bottle sizes</h3>
                <div className="space-y-3">
                  {pricingDraft.sizes.map((s, i) => (
                    <div
                      key={s._key || i}
                      className="border border-black/30 p-3 space-y-2"
                    >
                      <div className="flex gap-2 items-center">
                        <input
                          data-testid={`price-size-label-${i}`}
                          value={s.label}
                          onChange={(e) => {
                            const next = { ...pricingDraft };
                            next.sizes[i].label = e.target.value;
                            setPricingDraft(next);
                          }}
                          placeholder="20ml"
                          className="flex-1 border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493] font-serif-display font-bold"
                        />
                        <span className="overline text-[10px]">$</span>
                        <input
                          data-testid={`price-size-price-${i}`}
                          type="number"
                          step="0.01"
                          value={s.price}
                          onChange={(e) => {
                            const next = { ...pricingDraft };
                            next.sizes[i].price = parseFloat(e.target.value) || 0;
                            setPricingDraft(next);
                          }}
                          className="w-20 border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                        />
                        <button
                          onClick={() => {
                            const next = { ...pricingDraft };
                            next.sizes.splice(i, 1);
                            setPricingDraft(next);
                          }}
                          className="overline text-[10px] text-[#FF1493] underline"
                        >
                          remove
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="overline text-[10px] flex-1">
                          Scents included
                        </label>
                        <input
                          data-testid={`price-size-included-${i}`}
                          type="number"
                          min="0"
                          max="5"
                          value={s.included_scents ?? 1}
                          onChange={(e) => {
                            const next = { ...pricingDraft };
                            next.sizes[i].included_scents =
                              parseInt(e.target.value, 10) || 0;
                            setPricingDraft(next);
                          }}
                          className="w-20 border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setPricingDraft({
                        ...pricingDraft,
                        sizes: [
                          ...pricingDraft.sizes,
                          { _key: crypto.randomUUID(), label: "50ml", price: 95, included_scents: 5 },
                        ],
                      })
                    }
                    className="overline text-[10px] border border-black px-3 py-1 hover:bg-black hover:text-white"
                  >
                    + Add size
                  </button>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-[10px]">
                      Default included (legacy fallback)
                    </label>
                    <input
                      data-testid="price-included"
                      type="number"
                      value={pricingDraft.included_scents}
                      onChange={(e) =>
                        setPricingDraft({
                          ...pricingDraft,
                          included_scents:
                            parseInt(e.target.value, 10) || 0,
                        })
                      }
                      className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                    />
                  </div>
                  <div>
                    <label className="overline text-[10px]">
                      Price per extra scent
                    </label>
                    <input
                      data-testid="price-extra"
                      type="number"
                      step="0.01"
                      value={pricingDraft.extra_scent_price}
                      onChange={(e) =>
                        setPricingDraft({
                          ...pricingDraft,
                          extra_scent_price:
                            parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                    />
                  </div>
                </div>
              </div>

              {/* Event packages */}
              <div className="border border-black bg-white p-5">
                <h3 className="overline text-[10px] mb-3">Event packages</h3>
                <div className="space-y-4">
                  {pricingDraft.event_packages.map((p, i) => (
                    <div
                      key={p._key || i}
                      className="border border-black/30 p-3 space-y-2"
                    >
                      <input
                        data-testid={`pkg-label-${i}`}
                        value={p.label}
                        onChange={(e) => {
                          const next = { ...pricingDraft };
                          next.event_packages[i].label = e.target.value;
                          setPricingDraft(next);
                        }}
                        className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493] font-serif-display font-bold"
                      />
                      <div className="flex items-center gap-2">
                        <span className="overline text-[10px]">$</span>
                        <input
                          data-testid={`pkg-price-${i}`}
                          type="number"
                          step="0.01"
                          value={p.price}
                          onChange={(e) => {
                            const next = { ...pricingDraft };
                            next.event_packages[i].price =
                              parseFloat(e.target.value) || 0;
                            setPricingDraft(next);
                          }}
                          className="w-28 border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                        />
                        <button
                          onClick={() => {
                            const next = { ...pricingDraft };
                            next.event_packages.splice(i, 1);
                            setPricingDraft(next);
                          }}
                          className="ml-auto overline text-[10px] text-[#FF1493] underline"
                        >
                          remove
                        </button>
                      </div>
                      <textarea
                        data-testid={`pkg-desc-${i}`}
                        value={p.description}
                        rows={2}
                        onChange={(e) => {
                          const next = { ...pricingDraft };
                          next.event_packages[i].description = e.target.value;
                          setPricingDraft(next);
                        }}
                        className="w-full border border-black/30 bg-transparent p-2 text-sm outline-none focus:border-[#FF1493]"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setPricingDraft({
                        ...pricingDraft,
                        event_packages: [
                          ...pricingDraft.event_packages,
                          { _key: crypto.randomUUID(), label: "New package", price: 0, description: "" },
                        ],
                      })
                    }
                    className="overline text-[10px] border border-black px-3 py-1 hover:bg-black hover:text-white"
                  >
                    + Add package
                  </button>
                </div>

                <div className="mt-6 space-y-3">
                  <div>
                    <label className="overline text-[10px]">
                      Booking contact email
                    </label>
                    <input
                      data-testid="booking-contact-input"
                      value={pricingDraft.booking_contact}
                      onChange={(e) =>
                        setPricingDraft({
                          ...pricingDraft,
                          booking_contact: e.target.value,
                        })
                      }
                      className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                    />
                  </div>
                  <div>
                    <label className="overline text-[10px]">
                      Booking note (shown under packages on /events)
                    </label>
                    <textarea
                      data-testid="booking-note-input"
                      value={pricingDraft.booking_note}
                      onChange={(e) =>
                        setPricingDraft({
                          ...pricingDraft,
                          booking_note: e.target.value,
                        })
                      }
                      rows={2}
                      className="w-full border border-black/30 bg-transparent p-2 text-sm outline-none focus:border-[#FF1493]"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 flex justify-end">
                <button
                  data-testid="pricing-save"
                  onClick={savePricing}
                  className="bg-[#FF1493] text-white overline text-[11px] px-6 py-3 border border-black hover:bg-black"
                >
                  Save pricing
                </button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* RSVPS */}
        <TabsContent value="rsvps" className="mt-6">
          <h2 className="font-serif-display text-2xl font-bold mb-4">
            Event RSVPs ({rsvps.length})
          </h2>
          <div className="border border-black bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  {["Email", "Name", "When"].map((h) => (
                    <th key={h} className="text-left overline text-[10px] px-3 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rsvps.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-3 py-6 text-center text-black/50">
                      No RSVPs yet.
                    </td>
                  </tr>
                ) : (
                  rsvps.map((r) => (
                    <tr key={r.id} className="border-t border-black/15">
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.name || "—"}</td>
                      <td className="px-3 py-2 text-xs text-black/60">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SAVED BLENDS */}
        <TabsContent value="blends" className="mt-6">
          <h2 className="font-serif-display text-2xl font-bold mb-4">
            Saved blends ({blends.length})
          </h2>
          <div className="border border-black bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  {["Name", "Creator", "Notes", "Saved"].map((h) => (
                    <th key={h} className="text-left overline text-[10px] px-3 py-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blends.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-3 py-6 text-center text-black/50">
                      No blends saved yet.
                    </td>
                  </tr>
                ) : (
                  blends.map((b) => (
                    <tr key={b.id} className="border-t border-black/15">
                      <td className="px-3 py-2 font-serif-display font-bold">
                        {b.name}
                      </td>
                      <td className="px-3 py-2">
                        {b.creator_name?.trim() || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {b.scent_ids.map((id) => {
                            const sc = scents.find((s) => s.id === id);
                            return (
                              <span
                                key={id}
                                className="text-[10px] border border-black/40 px-1.5 overline"
                              >
                                {sc ? sc.symbol : "?"}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-black/60">
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-6">
          {!analytics ? (
            <div className="text-black/50">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Page views", v: analytics.total_pageviews },
                  { label: "Scent clicks", v: analytics.total_scent_clicks },
                  { label: "Selections", v: analytics.total_scent_selects },
                  { label: "Saved blends", v: analytics.total_blends },
                ].map((m) => (
                  <div
                    key={m.label}
                    className="border border-black bg-white p-5"
                    data-testid={`metric-${m.label.replace(/ /g, "-")}`}
                  >
                    <div className="overline text-[10px] text-black/60">
                      {m.label}
                    </div>
                    <div className="font-serif-display text-5xl font-black mt-1">
                      {m.v}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-black bg-white p-5">
                  <h3 className="overline text-[10px] mb-3">
                    Page views · last 14 days
                  </h3>
                  <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                      <BarChart data={analytics.daily_pageviews}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#0001" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(d) => d?.slice(5)}
                        />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#FF1493" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="border border-black bg-white p-5">
                  <h3 className="overline text-[10px] mb-3">Most-picked scents</h3>
                  {analytics.popular_scents.length === 0 ? (
                    <div className="text-black/50 text-sm">No selections yet.</div>
                  ) : (
                    <ul className="space-y-2">
                      {analytics.popular_scents.map((p) => (
                        <li
                          key={p.scent_id}
                          className="flex items-center justify-between border-b border-black/10 pb-2"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className="w-4 h-4 border border-black"
                              style={{ background: p.color || "#ccc" }}
                            />
                            <span className="font-serif-display font-bold">
                              {p.symbol || "??"}
                            </span>
                            <span>{p.name || "Removed scent"}</span>
                          </div>
                          <span className="overline text-[10px]">
                            {p.count}×
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* WAITLIST */}
        <TabsContent value="waitlist" className="mt-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="font-serif-display text-2xl font-bold">
                Waitlist ({waitlist.length})
              </h2>
              <p className="text-xs text-black/60 italic mt-1">
                Every pre-launch signup — the blend they were building is
                captured so you can pre-fill it in August.
              </p>
            </div>
            <button
              data-testid="waitlist-export-csv"
              onClick={() => {
                const header = [
                  "email",
                  "name",
                  "blend_name",
                  "scent_symbols",
                  "source",
                  "created_at",
                ];
                const rows = waitlist.map((w) => [
                  w.email,
                  w.name || "",
                  w.blend_name || "",
                  (w.scent_symbols || []).join("|"),
                  w.source || "",
                  w.created_at || "",
                ]);
                const escape = (v) =>
                  `"${String(v).replace(/"/g, '""')}"`;
                const csv = [header, ...rows]
                  .map((r) => r.map(escape).join(","))
                  .join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `briology_waitlist_${new Date()
                  .toISOString()
                  .slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("CSV downloaded");
              }}
              className="border border-black overline text-[10px] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            >
              Export CSV
            </button>
          </div>

          {waitlist.length === 0 ? (
            <div
              data-testid="waitlist-empty"
              className="text-black/50 border border-dashed border-black/40 p-8 text-center"
            >
              No waitlist signups yet — share your launching-soon link.
            </div>
          ) : (
            <div className="border border-black bg-white brutal-shadow overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-black text-white overline text-[9px]">
                  <tr>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">Blend building</th>
                    <th className="text-left px-3 py-2">Notes</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2 whitespace-nowrap">
                      When
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {waitlist.map((w, i) => (
                    <tr
                      key={w.id}
                      data-testid={`waitlist-row-${i}`}
                      className={`border-t border-black/10 ${
                        i % 2 === 0 ? "bg-white" : "bg-[#FFF8FA]"
                      }`}
                    >
                      <td className="px-3 py-2 font-medium">
                        <a
                          href={`mailto:${w.email}`}
                          className="text-[#FF1493] hover:underline"
                        >
                          {w.email}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-black/80">
                        {w.name || "—"}
                      </td>
                      <td className="px-3 py-2 font-serif-display italic text-black/85">
                        {w.blend_name || "—"}
                      </td>
                      <td className="px-3 py-2 overline text-[9px] text-black/60">
                        {(w.scent_symbols || []).join(" · ") || "—"}
                      </td>
                      <td className="px-3 py-2 overline text-[9px] text-black/55">
                        {w.source || "—"}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-black/60 whitespace-nowrap">
                        {w.created_at
                          ? new Date(w.created_at).toLocaleString([], {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Scent edit dialog */}
      <Dialog open={!!editingScent} onOpenChange={(o) => !o && setEditingScent(null)}>
        <DialogContent className="bg-white border border-black rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl font-black">
              {editingScent?.new ? "New scent" : `Edit ${editingScent?.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <label className="overline text-[10px]">Symbol</label>
              <input
                data-testid="form-symbol"
                value={scentForm.symbol}
                maxLength={3}
                onChange={(e) =>
                  setScentForm({ ...scentForm, symbol: e.target.value })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Name</label>
              <input
                data-testid="form-name"
                value={scentForm.name}
                onChange={(e) => setScentForm({ ...scentForm, name: e.target.value })}
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Category</label>
              <input
                data-testid="form-category"
                value={scentForm.category}
                onChange={(e) =>
                  setScentForm({ ...scentForm, category: e.target.value })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={scentForm.color}
                  onChange={(e) =>
                    setScentForm({ ...scentForm, color: e.target.value })
                  }
                  className="w-10 h-9 border border-black"
                />
                <input
                  data-testid="form-color"
                  value={scentForm.color}
                  onChange={(e) =>
                    setScentForm({ ...scentForm, color: e.target.value })
                  }
                  className="flex-1 border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
                />
              </div>
            </div>
            <div className="col-span-2">
              <label className="overline text-[10px]">Notes (comma-separated)</label>
              <input
                data-testid="form-notes"
                value={
                  Array.isArray(scentForm.notes)
                    ? scentForm.notes.join(", ")
                    : scentForm.notes
                }
                onChange={(e) =>
                  setScentForm({ ...scentForm, notes: e.target.value })
                }
                placeholder="Amber, Jasmine, Musk"
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div className="col-span-2">
              <label className="overline text-[10px]">Description</label>
              <textarea
                data-testid="form-description"
                value={scentForm.description}
                onChange={(e) =>
                  setScentForm({ ...scentForm, description: e.target.value })
                }
                rows={2}
                className="w-full border border-black/30 bg-transparent p-2 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input
                id="bestseller"
                data-testid="form-bestseller"
                type="checkbox"
                checked={!!scentForm.bestseller}
                onChange={(e) =>
                  setScentForm({ ...scentForm, bestseller: e.target.checked })
                }
              />
              <label htmlFor="bestseller" className="overline text-[10px]">
                Bestseller — show ribbon in hover popover
              </label>
            </div>
            <div className="col-span-2">
              <label className="overline text-[10px]">Image (optional, ≤1MB)</label>
              <input
                data-testid="form-image"
                type="file"
                accept="image/*"
                onChange={onUploadImage}
                className="block text-xs"
              />
              {scentForm.image_url && (
                <img
                  src={scentForm.image_url}
                  alt="preview"
                  className="mt-2 w-16 h-16 object-cover border border-black"
                />
              )}
            </div>
          </div>
          <DialogFooter className="mt-6 gap-2">
            <button
              onClick={() => setEditingScent(null)}
              className="overline text-[11px] border border-black px-5 py-2 hover:bg-black hover:text-white"
            >
              Cancel
            </button>
            <button
              data-testid="form-save"
              onClick={saveScent}
              className="overline text-[11px] bg-[#FF1493] text-white px-5 py-2 border border-black hover:bg-black"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event edit dialog */}
      <Dialog open={!!editingEvent} onOpenChange={(o) => !o && setEditingEvent(null)}>
        <DialogContent className="bg-white border border-black rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl font-black">
              {editingEvent?.new ? "New event" : "Edit event"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 mt-2">
            <div>
              <label className="overline text-[10px]">Tag</label>
              <input
                value={eventForm.tag}
                onChange={(e) => setEventForm({ ...eventForm, tag: e.target.value })}
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Title</label>
              <input
                value={eventForm.title}
                onChange={(e) =>
                  setEventForm({ ...eventForm, title: e.target.value })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Date (text)</label>
              <input
                value={eventForm.date_text}
                onChange={(e) =>
                  setEventForm({ ...eventForm, date_text: e.target.value })
                }
                placeholder="Spring 2026"
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Description</label>
              <textarea
                value={eventForm.description}
                onChange={(e) =>
                  setEventForm({ ...eventForm, description: e.target.value })
                }
                rows={3}
                className="w-full border border-black/30 bg-transparent p-2 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Order (sort)</label>
              <input
                type="number"
                value={eventForm.order}
                onChange={(e) =>
                  setEventForm({ ...eventForm, order: Number(e.target.value) || 0 })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
          </div>
          <DialogFooter className="mt-6 gap-2">
            <button
              onClick={() => setEditingEvent(null)}
              className="overline text-[11px] border border-black px-5 py-2 hover:bg-black hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={saveEvent}
              className="overline text-[11px] bg-[#FF1493] text-white px-5 py-2 border border-black hover:bg-black"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Featured blend edit dialog */}
      <Dialog open={!!editingFB} onOpenChange={(o) => !o && setEditingFB(null)}>
        <DialogContent className="bg-white border border-black rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl font-black">
              {editingFB?.new ? "New popular blend" : "Edit popular blend"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 mt-2">
            <div>
              <label className="overline text-[10px]">Name</label>
              <input
                value={fbForm.name}
                onChange={(e) => setFbForm({ ...fbForm, name: e.target.value })}
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Category</label>
              <input
                value={fbForm.category}
                onChange={(e) => setFbForm({ ...fbForm, category: e.target.value })}
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Tagline</label>
              <input
                value={fbForm.tagline}
                onChange={(e) => setFbForm({ ...fbForm, tagline: e.target.value })}
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">
                Scent symbols (comma-separated) — e.g. Vs, Cs, Ff, Hr
              </label>
              <input
                value={
                  Array.isArray(fbForm.scent_symbols)
                    ? fbForm.scent_symbols.join(", ")
                    : fbForm.scent_symbols
                }
                onChange={(e) =>
                  setFbForm({ ...fbForm, scent_symbols: e.target.value })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
            <div>
              <label className="overline text-[10px]">Order (sort)</label>
              <input
                type="number"
                value={fbForm.order}
                onChange={(e) =>
                  setFbForm({ ...fbForm, order: Number(e.target.value) || 0 })
                }
                className="w-full border-b border-black bg-transparent py-1 outline-none focus:border-[#FF1493]"
              />
            </div>
          </div>
          <DialogFooter className="mt-6 gap-2">
            <button
              onClick={() => setEditingFB(null)}
              className="overline text-[11px] border border-black px-5 py-2 hover:bg-black hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={saveFB}
              className="overline text-[11px] bg-[#FF1493] text-white px-5 py-2 border border-black hover:bg-black"
            >
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
